/**
 * Application Configuration — Donor Portal
 *
 * Runtime values injected via `window.__ENV__` (populated by docker-entrypoint.sh)
 * take precedence over build-time `import.meta.env` values so the same Docker
 * image can be deployed to any environment.
 */

import { API_ROUTES } from './api-routes';

/** Runtime env injected by docker-entrypoint.sh → env-config.js */
const runtimeEnv: Record<string, string | undefined> =
  typeof window !== 'undefined'
    ? ((window as unknown as Record<string, unknown>).__ENV__ as Record<string, string | undefined> ?? {})
    : {};

/** Return the first non-empty string, or fallback. */
export function env(key: string, fallback = ''): string {
  return runtimeEnv[key] || import.meta.env[key] || fallback;
}

export const config = {
  app: {
    name: 'TMS Donor Portal',
    version: env('VITE_APP_VERSION', '1.0.0'),
    environment: import.meta.env.MODE,
  },
  api: {
    baseUrl: env('VITE_API_URL', 'http://localhost:3000'),
    timeout: 30000,
  },
  /** Default hospital context -- set via VITE_HOSPITAL_ID env var. */
  hospitalId: env('VITE_HOSPITAL_ID'),
  /** Default enterprise context -- set via VITE_ENTERPRISE_ID env var. */
  enterpriseId: env('VITE_ENTERPRISE_ID'),
  /** Default tenant context -- set via VITE_TENANT_ID env var. */
  tenantId: env('VITE_TENANT_ID'),
  /** Default staff ID -- set via VITE_STAFF_ID env var. */
  staffId: env('VITE_STAFF_ID'),
  /** Donor person ID — populated at runtime by setAuthContext() after login. */
  personId: '',
  /**
   * UUID of the user creating a record (maps to `created_by` in API payloads).
   * Falls back to VITE_STAFF_ID when VITE_CREATED_BY is not set.
   */
  createdBy: env('VITE_CREATED_BY') || env('VITE_STAFF_ID'),
  logging: {
    level: (import.meta.env.DEV ? 'debug' : 'info') as 'debug' | 'info',
    console: true,
    beaconUrl: import.meta.env.PROD ? `${API_ROUTES.SERVICE}/logs` : undefined,
    beaconLevel: 'warn' as const,
    serviceName: 'donor-portal',
  },
  telemetry: {
    enabled: !!env('NEW_RELIC_BROWSER_APP_ID'),
    serviceName: 'donor-portal',
    environment: import.meta.env.MODE,
    beaconUrl: `${API_ROUTES.SERVICE}/telemetry`,
    applicationId: env('NEW_RELIC_BROWSER_APP_ID'),
    licenseKey: env('NEW_RELIC_BROWSER_LICENSE_KEY'),
  },
  /** Preferred language (e.g. 'en', 'en-GB', 'en-US', 'de', 'fr'). */
  preferredLanguage: env('VITE_PREFERRED_LANGUAGE', 'en'),
  features: {
    enableDevTools: import.meta.env.DEV,
    enableMockData: env('VITE_ENABLE_MOCK') === 'true',
  },
};

export type AppConfig = typeof config;

/**
 * Update auth-derived config values after login or on page-reload hydration.
 *
 * Call this after receiving tokens from the auth callback so that
 * `config.personId` reflects the authenticated donor's context instead of
 * the build-time env-var default.
 */
export function setAuthContext(ctx: { personId?: string }): void {
  // Always write — even when clearing — so a stale donor ID from a previous
  // session can never bleed into a new login that lacks person_id.
  (config as Record<string, unknown>).personId = ctx.personId ?? '';
}

/**
 * Clear the donor context on logout.
 *
 * Resets `config.personId` to an empty string. There is no env-var fallback —
 * the value is always sourced from the /auth/login response at runtime.
 */
export function clearAuthContext(): void {
  (config as Record<string, unknown>).personId = '';
}

/**
 * Return the configured person ID or throw if it is missing.
 * Shared guard used by every service that requires a donor context.
 */
export function requirePersonId(): string {
  const id = config.personId;
  if (!id) {
    throw new Error('person_id is not available. Ensure you are logged in as a donor.');
  }
  return id;
}
